import React, { ButtonHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';

export type ButtonColor =
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'blue';
export type ButtonVariant = 'contained' | 'outlined' | 'text' | 'soft';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonActionProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const colorVariantClasses: Record<
  ButtonColor,
  Record<ButtonVariant, string>
> = {
  primary: {
    contained:
      'bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600',
    outlined: 'border border-indigo-600 text-indigo-600 hover:bg-indigo-50',
    text: 'text-indigo-600 hover:bg-indigo-50',
    soft: 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100',
  },
  success: {
    contained:
      'bg-emerald-600 text-white hover:bg-emerald-500 focus-visible:outline-emerald-600',
    outlined: 'border border-emerald-600 text-emerald-600 hover:bg-emerald-50',
    text: 'text-emerald-600 hover:bg-emerald-50',
    soft: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100',
  },
  warning: {
    contained:
      'bg-amber-500 text-white hover:bg-amber-400 focus-visible:outline-amber-500',
    outlined: 'border border-amber-500 text-amber-500 hover:bg-amber-50',
    text: 'text-amber-500 hover:bg-amber-50',
    soft: 'bg-amber-50 text-amber-500 hover:bg-amber-100',
  },
  danger: {
    contained:
      'bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-600',
    outlined: 'border border-red-600 text-red-600 hover:bg-red-50',
    text: 'text-red-600 hover:bg-red-50',
    soft: 'bg-red-50 text-red-600 hover:bg-red-100',
  },
  info: {
    contained:
      'bg-sky-600 text-white hover:bg-sky-500 focus-visible:outline-sky-600',
    outlined: 'border border-sky-600 text-sky-600 hover:bg-sky-50',
    text: 'text-sky-600 hover:bg-sky-50',
    soft: 'bg-sky-50 text-sky-600 hover:bg-sky-100',
  },
  blue: {
    contained:
      'bg-blue-500 text-white hover:bg-blue-400 focus-visible:outline-blue-500',
    outlined: 'border border-blue-500 text-blue-500 hover:bg-blue-50',
    text: 'text-blue-500 hover:bg-blue-50',
    soft: 'bg-blue-50 text-blue-500 hover:bg-blue-100',
  },
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-3.5 py-2 text-sm',
  lg: 'px-4 py-2.5 text-base',
};

export default function ButtonAction({
  variant = 'contained',
  color = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  disabled,
  children,
  startIcon,
  endIcon,
  ...props
}: ButtonActionProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={twMerge(
        'inline-flex items-center justify-center gap-2 rounded-md font-semibold',
        'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        'transition-all duration-200 ease-in-out',
        variant !== 'text' && 'shadow-sm',
        colorVariantClasses[color][variant],
        sizeClasses[size],
        fullWidth ? 'w-full' : '',
        className,
      )}
      {...props}
    >
      {startIcon && <span className="inline-flex shrink-0">{startIcon}</span>}
      {children}
      {endIcon && <span className="inline-flex shrink-0">{endIcon}</span>}
    </button>
  );
}
